// src/components/workspace/AssistantPanel.tsx
import { useRef, useEffect, useState, useCallback } from "react";
import { X, Maximize2, PencilLine, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAssistantPanel } from "@/hooks/workspace";
import { useAssistantController } from "@/hooks/assistant/useAssistantController";
import { useAssistantStore } from "@/stores/assistantStore";
import AssistantChat from "@/components/assistant/AssistantChat";
import WorkspaceChat from "@/components/assistant/WorkspaceChat";
import SheetsChat from "@/components/assistant/SheetsChat";
import ConversationsPopup from "@/components/assistant/ConversationsPopup";
import { PanelTabSelector, type AssistantMode, type PanelTab } from "@/components/assistant/ModeSelector";

// Panel widths per tab
const PANEL_WIDTHS: Record<PanelTab, number> = {
  ask: 520,
  work: 560,
  sheets: 580,
};

// Min/max constraints for resizing
const MIN_PANEL_WIDTH = 380;
const MAX_PANEL_WIDTH = 800;

export function AssistantPanel() {
  const navigate = useNavigate();
  const { isOpen, setOpen } = useAssistantPanel();
  const setPreserveOnUnmount = useAssistantStore((s) => s.setPreserveOnUnmount);

  const [showHistory, setShowHistory] = useState(false);
  // Track the active panel tab (ask vs work)
  const [activeTab, setActiveTab] = useState<PanelTab>("ask");
  // Track the research depth for Ask mode (quick vs deep)
  const [isDeepResearch, setIsDeepResearch] = useState(true);
  const [prefill, setPrefill] = useState<string | undefined>(undefined);
  const [customWidth, setCustomWidth] = useState<number | null>(MAX_PANEL_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);

  // Derive the actual mode from tab + depth toggle
  const mode: AssistantMode = activeTab === "work"
    ? "workspace"
    : activeTab === "sheets"
    ? "sheets"
    : isDeepResearch ? "deep" : "quick";

  // Handle mode change from child components
  const handleModeChange = useCallback((newMode: AssistantMode) => {
    if (newMode === "workspace") {
      setActiveTab("work");
    } else if (newMode === "sheets") {
      setActiveTab("sheets");
    } else {
      setActiveTab("ask");
      setIsDeepResearch(newMode === "deep");
    }
  }, []);

  // Handle tab change - start new chat when switching to Work mode
  const handleTabChange = useCallback((tab: PanelTab, startNew?: () => void) => {
    const wasAsk = activeTab === "ask";
    const isWork = tab === "work";

    setActiveTab(tab);

    // Reset to quick when switching to ask tab
    if (tab === "ask") {
      setIsDeepResearch(false);
    }

    // Start new chat when switching from Ask to Work (they have separate flows)
    if (wasAsk && isWork && startNew) {
      startNew();
    }
  }, [activeTab]);

  const {
    activeConversationId,
    streamingById,
    conversationsLoading,
    sortedConversations,
    setActive,
    startNewChat,
    deleteConversation,
    chatProps,
    endRef,
  } = useAssistantController();

  // Panel width: use custom width if set, otherwise tab-based default
  const panelWidth = customWidth ?? PANEL_WIDTHS[activeTab];

  // Handle resize drag
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = panelWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidth + delta));
      setCustomWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  }, [panelWidth]);

  // Dismiss history popup on outside click
  useEffect(() => {
    if (!showHistory) return;
    function handleClickOutside(e: MouseEvent) {
      if (!popupRef.current) return;
      const target = e.target as Node | null;
      if (target && !popupRef.current.contains(target)) {
        setShowHistory(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showHistory]);

  // Ensure history popup is closed whenever the panel opens
  useEffect(() => {
    if (isOpen) setShowHistory(false);
  }, [isOpen]);

  // Listen for workspace mode event (from Widget Library redirect)
  useEffect(() => {
    const handleWorkspaceMode = () => {
      setActiveTab("work");
    };
    window.addEventListener("abi:assistant:workspace-mode", handleWorkspaceMode);
    return () => window.removeEventListener("abi:assistant:workspace-mode", handleWorkspaceMode);
  }, []);

  // Listen for sheets mode event (from SheetsPage Ask AI button)
  useEffect(() => {
    const handleSheetsMode = () => {
      setActiveTab("sheets");
    };
    window.addEventListener("abi:assistant:sheets-mode", handleSheetsMode);
    return () => window.removeEventListener("abi:assistant:sheets-mode", handleSheetsMode);
  }, []);

  // Clear prefill after it's used
  const handlePrefillUsed = useCallback(() => {
    setPrefill(undefined);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed top-14 right-0 bottom-0 z-40 ${isResizing ? "" : "transition-all duration-200 ease-out"}`}
      style={{ width: panelWidth, maxWidth: "calc(100vw - 280px)" }}
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-violet-500/50 transition-colors z-10 group"
        onMouseDown={handleResizeStart}
      >
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-gray-600 group-hover:bg-violet-400 transition-colors" />
      </div>

      <div className="h-full bg-gray-900 border-l border-white/10 flex flex-col relative">
        {/* Floating top controls */}
        <div className="absolute top-3 left-3 right-3 z-20 flex items-center justify-between">
          {/* Left: History & New */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center justify-center h-8 w-8 rounded-lg bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 text-gray-400 hover:text-white transition-colors border border-white/10"
              onClick={() => setShowHistory((v) => !v)}
              aria-label="History"
              title="History"
            >
              <History className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="flex items-center justify-center h-8 w-8 rounded-lg bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 text-gray-400 hover:text-white transition-colors border border-white/10"
              onClick={startNewChat}
              aria-label="New chat"
              title="New chat"
            >
              <PencilLine className="h-4 w-4" />
            </button>
          </div>

          {/* Center: Tab Selector */}
          <PanelTabSelector activeTab={activeTab} onTabChange={(tab) => handleTabChange(tab, startNewChat)} />

          {/* Right: Expand & Close */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center justify-center h-8 w-8 rounded-lg bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 text-gray-400 hover:text-white transition-colors border border-white/10"
              onClick={() => {
                setPreserveOnUnmount(true);
                setOpen(false);
                navigate("/app/assistant");
              }}
              aria-label="Expand to full page"
              title="Expand"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="flex items-center justify-center h-8 w-8 rounded-lg bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 text-gray-400 hover:text-white transition-colors border border-white/10"
              onClick={() => setOpen(false)}
              aria-label="Close"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* History popup */}
        {showHistory && (
          <div
            ref={popupRef}
            className="absolute top-14 left-3 w-72 rounded-lg bg-gray-900 shadow-xl border border-white/10 p-2 z-50"
          >
            <ConversationsPopup
              conversations={sortedConversations}
              activeConversationId={activeConversationId}
              onSelectConversation={(cid) => {
                setActive(cid);
                setShowHistory(false);
              }}
              loading={conversationsLoading}
              streamingById={streamingById}
              onDeleteConversation={deleteConversation}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden pt-14">
          {mode === "workspace" ? (
            <WorkspaceChat
              prefill={prefill}
              onPrefillUsed={handlePrefillUsed}
              compact
            />
          ) : mode === "sheets" ? (
            <SheetsChat
              prefill={prefill}
              onPrefillUsed={handlePrefillUsed}
              compact
            />
          ) : (
            <AssistantChat
              {...chatProps}
              mode={mode}
              onModeChange={handleModeChange}
              prefill={prefill}
              onPrefillUsed={handlePrefillUsed}
              panelMode
              isDeepResearch={isDeepResearch}
              onDepthToggle={setIsDeepResearch}
            />
          )}
          <div ref={endRef} />
        </div>
      </div>
    </div>
  );
}

export default AssistantPanel;
